/**
 * SearchPanel - Search within PDF document
 * Searches through layout_data blocks and highlights matches
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  NavigateNext as NavIcon
} from '@mui/icons-material';
import { Block, SearchResult } from './types';

interface SearchPanelProps {
  open: boolean;
  onClose: () => void;
  blocks: Block[];
  onNavigate: (page: number, blockId?: string) => void;
}

const SearchPanel: React.FC<SearchPanelProps> = ({
  open,
  onClose,
  blocks,
  onNavigate
}) => {
  const [query, setQuery] = useState('');

  // Clean text for display
  const cleanText = (str: string): string => {
    const temp = document.createElement('div');
    temp.innerHTML = str.replace(/<br\s*\/?>/gi, ' ').replace(/\n/g, ' ');
    return (temp.textContent || temp.innerText || '').replace(/\s+/g, ' ').trim();
  };

  // Search through blocks
  const searchResults = useMemo(() => {
    if (!query.trim() || query.length < 2) return [];

    const results: SearchResult[] = [];
    const queryLower = query.toLowerCase();

    blocks.forEach(block => {
      const text = cleanText(block.html || block.text || block.figure_caption || '');
      const textLower = text.toLowerCase();

      if (textLower.includes(queryLower)) {
        // Extract page number from block ID
        const pageMatch = block.id.match(/\/page\/(\d+)/);
        const pageNum = pageMatch ? parseInt(pageMatch[1]) + 1 : 1;

        // Get context around match
        const matchIndex = textLower.indexOf(queryLower);
        const start = Math.max(0, matchIndex - 40);
        const end = Math.min(text.length, matchIndex + query.length + 40);
        const context = (start > 0 ? '...' : '') +
          text.slice(start, end) +
          (end < text.length ? '...' : '');

        results.push({
          page: pageNum,
          text: text.slice(matchIndex, matchIndex + query.length),
          context,
          blockId: block.id
        });
      }
    });

    // Sort by page number and limit results
    return results.sort((a, b) => a.page - b.page).slice(0, 50);
  }, [query, blocks]);

  const handleResultClick = useCallback((result: SearchResult) => {
    onNavigate(result.page, result.blockId);
    onClose();
  }, [onNavigate, onClose]);

  // Highlight query in context
  const highlightMatch = (context: string, matchText: string) => {
    const lowerContext = context.toLowerCase();
    const lowerMatch = matchText.toLowerCase();
    const matchIndex = lowerContext.indexOf(lowerMatch);

    if (matchIndex === -1) return context;

    return (
      <>
        {context.slice(0, matchIndex)}
        <Box
          component="span"
          sx={{ bgcolor: 'rgba(255, 235, 59, 0.6)', fontWeight: 600 }}
        >
          {context.slice(matchIndex, matchIndex + matchText.length)}
        </Box>
        {context.slice(matchIndex + matchText.length)}
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { height: '70vh', maxHeight: 600 }
      }}
    >
      <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Search in Document</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {/* Search Input */}
        <TextField
          fullWidth
          autoFocus
          placeholder="Search text..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2
            }
          }}
        />

        {/* Results Count */}
        {query.length >= 2 && (
          <Typography variant="body2" color="text.secondary">
            {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
          </Typography>
        )}

        {/* Results List */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {query.length < 2 ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary'
              }}
            >
              <Typography>Type at least 2 characters to search</Typography>
            </Box>
          ) : searchResults.length === 0 ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary'
              }}
            >
              <Typography>No results found</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {searchResults.map((result, i) => (
                <ListItem key={i} disablePadding divider>
                  <ListItemButton onClick={() => handleResultClick(result)}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={`Page ${result.page}`}
                            size="small"
                            sx={{ bgcolor: '#E3F2FD', minWidth: 70 }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.5,
                            fontSize: 13,
                            lineHeight: 1.4
                          }}
                        >
                          {highlightMatch(result.context, result.text)}
                        </Typography>
                      }
                    />
                    <NavIcon color="action" />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SearchPanel;
